import React, { useState } from 'react';
import { Box, Collapse, IconButton, InputAdornment, styled, Switch, TextField, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, SearchOutlined } from '@mui/icons-material';
import { AccessRule } from 'types/accessRule';
import { countRules, filterRules } from './profileRules';

const Group = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  overflow: 'hidden',
}));

const GroupHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.5),
  background: theme.palette.grey[50],
}));

const Line = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 0),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

interface RuleLinesProps {
  rules: AccessRule[];
  depth: number;
  onToggle(id: number): void;
}

const RuleLines: React.FC<RuleLinesProps> = ({ rules, depth, onToggle }) => (
  <>
    {rules.map(rule => (
      <React.Fragment key={rule.id}>
        <Line sx={{ pl: depth * 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={depth === 0 ? 700 : 400}>
              {rule.title}
            </Typography>
            {rule.description && (
              <Typography variant="caption" color="textSecondary" display="block">
                {rule.description}
              </Typography>
            )}
          </div>
          <Switch size="small" color="primary" checked={!!rule.active} onChange={() => onToggle(rule.id)} />
        </Line>
        <RuleLines rules={rule.access_rules} depth={depth + 1} onToggle={onToggle} />
      </React.Fragment>
    ))}
  </>
);

interface ProfilePermissionsProps {
  // regras de nível mais alto, cada uma com as filhas aninhadas
  rules: AccessRule[];
  onToggle(id: number): void;
}

// Permissões do perfil agrupadas por módulo, com busca e contagem de ativas.
const ProfilePermissions: React.FC<ProfilePermissionsProps> = ({ rules, onToggle }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const visible = filterRules(rules, search);
  const searching = search.trim().length > 0;

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Permissões
        </Typography>
        <TextField
          size="small"
          placeholder="Buscar permissão"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ maxWidth: 260, width: '100%' }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {visible.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
          Nenhuma permissão encontrada.
        </Typography>
      )}

      {visible.map(rule => {
        // a contagem mostra o grupo inteiro, mesmo quando a busca esconde parte dele
        const original = rules.find(item => item.id === rule.id) || rule;
        const { active, total } = countRules([original]);
        const expanded = searching || !!open[rule.id];

        return (
          <Group key={rule.id}>
            <GroupHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <IconButton
                  size="small"
                  aria-label={expanded ? 'Recolher' : 'Expandir'}
                  onClick={() => setOpen(old => ({ ...old, [rule.id]: !old[rule.id] }))}
                >
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {rule.title}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                  {active} de {total}
                </Typography>
                <Switch size="small" color="primary" checked={!!original.active} onChange={() => onToggle(rule.id)} />
              </Box>
            </GroupHeader>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ px: 1.5, pb: 0.5 }}>
                <RuleLines rules={rule.access_rules} depth={0} onToggle={onToggle} />
                {rule.access_rules.length === 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', py: 1 }}>
                    {rule.description || 'Sem regras filhas.'}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Group>
        );
      })}
    </div>
  );
};

export default ProfilePermissions;
